import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  padding: 40px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 15px 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 0;
  opacity: 0.9;
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  margin: 0 0 20px 0;
  border-bottom: 3px solid #667eea;
  padding-bottom: 10px;
`;

const FAQItem = styled.div`
  margin: 25px 0;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
`;

const Question = styled.div`
  background: #f8f9fa;
  padding: 18px 20px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: between;
  align-items: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
  
  &::after {
    content: '${props => props.isOpen ? '−' : '+'}';
    font-size: 1.5rem;
    margin-left: auto;
    color: #667eea;
  }
`;

const Answer = styled.div`
  padding: 20px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  line-height: 1.6;
  color: #666;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin: 15px 0;
  padding: 15px;
  background: #f0f8ff;
  border-radius: 8px;
  border-left: 4px solid #667eea;
`;

const BenefitIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 15px;
  color: #667eea;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  margin: 30px 0;
`;

const CTATitle = styled.h3`
  font-size: 1.8rem;
  margin: 0 0 15px 0;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  margin: 0 0 25px 0;
  opacity: 0.9;
`;

const ReferralHighlight = styled.div`
  background: #fff3cd;
  border: 2px solid #ffeaa7;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
`;

const ReferralAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #e17055;
  margin-bottom: 10px;
`;

const LegalRightsBox = styled.div`
  background: #d4edda;
  border: 2px solid #c3e6cb;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const LegalTitle = styled.h4`
  color: #155724;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const LegalText = styled.p`
  color: #155724;
  margin: 10px 0;
  line-height: 1.5;
`;

const TenantFAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  // Update page meta tags for social sharing
  useEffect(() => {
    // Update page title
    document.title = 'ShowingBooker: Tenant Rights - Take Control of Your Property Showings';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Know your rights as a tenant. Get proper 48+ hour notice, legal compliance, and professional treatment for property showings. Sample letter included.';
    
    // Update Open Graph meta tags for social sharing (iPhone preview)
    const updateMetaTag = (property, content) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    };
    
    updateMetaTag('og:title', 'ShowingBooker: Tenant Rights');
    updateMetaTag('og:description', 'Know your rights as a tenant. Get proper 48+ hour notice, legal compliance, and professional treatment for property showings. Sample letter included.');
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:site_name', 'ShowingBooker');
    
    // Twitter Card meta tags
    const updateTwitterTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    };
    
    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', 'ShowingBooker: Tenant Rights');
    updateTwitterTag('twitter:description', 'Know your rights as a tenant. Get proper 48+ hour notice, legal compliance, and professional treatment for property showings. Sample letter included.');
    
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What is ShowingBooker and how does it protect me as a tenant?",
      answer: `ShowingBooker is a professional property showing management system that ensures your legal rights are respected when your rental property is shown to potential buyers or renters. We provide legal notice (48+ hours advance notice), coordinate with all parties, and create a paper trail to protect your quiet enjoyment rights.`
    },
    {
      question: "Why should I insist my landlord/agent use ShowingBooker?",
      answer: `ShowingBooker ensures compliance with California Civil Code Section 1954, prevents surprise visits, gives you control over your schedule, and creates documentation you can use if there are disputes. It's the professional standard for property showings that protects both you and the agent.`
    },
    {
      question: "What are my legal rights regarding property showings?",
      answer: `Under California law, you have the right to 24-hour written notice (we provide 48+ hours), the right to be present during showings, and the right to quiet enjoyment of your rental. Landlords can only enter for specific legal reasons like showing to prospective buyers or conducting necessary repairs.`
    },
    {
      question: "What if my landlord or agent doesn't want to use ShowingBooker?",
      answer: `You can share this page with them to show the benefits. If they continue with unprofessional practices, ShowingBooker provides documentation and legal compliance that helps protect your rights. You can also refer them to speak with other agents who use our professional system.`
    },
    {
      question: "How does the referral program work?",
      answer: `When you refer someone (friend, family, neighbor) whose landlord or agent signs up for ShowingBooker, you earn $5 for every showing they book moving forward! This creates ongoing income while helping others get the professional treatment they deserve.`
    },
    {
      question: "What documentation does ShowingBooker provide?",
      answer: `Every showing generates legally compliant notices, email confirmations, SMS notifications, and optional certified mail. This creates a complete paper trail that demonstrates proper notice was given and procedures were followed - valuable if you ever need to document issues.`
    },
    {
      question: "Can I use ShowingBooker notifications to report problems?",
      answer: `Yes! The email and SMS notifications, along with certified mail receipts, provide timestamped evidence of when proper notice was given. If there are too many showings or inappropriate timing, these records can be valuable when contacting local tenant rights organizations or city authorities.`
    },
    {
      question: "What should I do during a ShowingBooker scheduled showing?",
      answer: `You're encouraged to be present to ensure your belongings are safe and the showing goes smoothly. Turn on lights, secure pets, and ensure doors are accessible. You help control the process while the system ensures everyone follows proper procedures.`
    }
  ];

  return (
    <Container>
      <Header>
        <Title>Take Control of Your Showings</Title>
        <Subtitle>Know your rights. Protect your privacy. Get proper notice.</Subtitle>
      </Header>

      <ContentSection>
        <SectionTitle>🏠 Why Tenants Love ShowingBooker</SectionTitle>
        <BenefitsList>
          <BenefitItem>
            <BenefitIcon>📋</BenefitIcon>
            <div>
              <strong>Professional Notice:</strong> Always get 48+ hours advance notice with all details - no more surprise visits or last-minute disruptions
            </div>
          </BenefitItem>
          <BenefitItem>
            <BenefitIcon>⚖️</BenefitIcon>
            <div>
              <strong>Legal Compliance:</strong> Full California Civil Code Section 1954 compliance with documented proof of proper procedures
            </div>
          </BenefitItem>
          <BenefitItem>
            <BenefitIcon>🔒</BenefitIcon>
            <div>
              <strong>Control & Safety:</strong> Know exactly who's coming, when, and why - be present or reserve the right to be present to protect your belongings and privacy
            </div>
          </BenefitItem>
          <BenefitItem>
            <BenefitIcon>📧</BenefitIcon>
            <div>
              <strong>Paper Trail:</strong> Email confirmations, SMS notifications, and optional certified mail create documentation you can use if needed
            </div>
          </BenefitItem>
          <BenefitItem>
            <BenefitIcon>💰</BenefitIcon>
            <div>
              <strong>Accountability:</strong> Buyer's agents/buyers pay $15 per showing to the platform for compliance documentation and legal notices, ensuring they're serious and professional - no more casual "drop-bys"
            </div>
          </BenefitItem>
        </BenefitsList>
      </ContentSection>

      <ContentSection>
        <SectionTitle>💡 Know Your California Tenant Rights</SectionTitle>
        <LegalRightsBox>
          <LegalTitle>California Civil Code Section 1954 - Your Legal Protections:</LegalTitle>
          <LegalText>
            <strong>• 24-Hour Notice Required:</strong> Landlords must give written notice at least 24 hours before entry (ShowingBooker provides 48+ hours)
          </LegalText>
          <LegalText>
            <strong>• Limited Reasons for Entry:</strong> Entry is only allowed for specific legal purposes like showing to prospective buyers, making necessary repairs, or emergencies
          </LegalText>
          <LegalText>
            <strong>• Reasonable Hours:</strong> Entry must be during normal business hours unless it's an emergency
          </LegalText>
          <LegalText>
            <strong>• Right to Be Present:</strong> You have the right to be present during any showing or inspection
          </LegalText>
          <LegalText>
            <strong>• Quiet Enjoyment:</strong> You have the fundamental right to use and enjoy your rental without unnecessary interference
          </LegalText>
        </LegalRightsBox>
      </ContentSection>

      <ContentSection>
        <SectionTitle>💸 Earn Money by Helping Others</SectionTitle>
        <ReferralHighlight>
          <ReferralAmount>$5 per showing - Forever!</ReferralAmount>
          <p><strong>Know friends or family dealing with inappropriate showing practices?</strong></p>
          <p>When you refer someone whose agent signs up for ShowingBooker, you earn $5 for every showing they book moving forward. Help others get professional treatment while earning ongoing income!</p>
        </ReferralHighlight>
        <p>
          <strong>Common problems ShowingBooker solves:</strong> Unannounced visits, insufficient notice, 
          unprofessional agents, too many showings, lack of coordination, no documentation, 
          disrespectful timing, and landlords who ignore tenant rights.
        </p>
      </ContentSection>

      <ContentSection>
        <SectionTitle>❓ Frequently Asked Questions</SectionTitle>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <Question 
              isOpen={openFAQ === index} 
              onClick={() => toggleFAQ(index)}
            >
              {faq.question}
            </Question>
            <Answer isOpen={openFAQ === index}>
              {faq.answer}
            </Answer>
          </FAQItem>
        ))}
      </ContentSection>

      <CTASection>
        <CTATitle>Ready to Take Control?</CTATitle>
        <CTAText>
          Share ShowingBooker with your landlord, property manager, or real estate agent. 
          Insist on professional standards. Protect your rights.
        </CTAText>
        <CTAText>
          <strong>It's your home. You deserve proper notice and professional treatment.</strong>
        </CTAText>
        <p style={{ marginTop: '30px', fontSize: '1.1rem', fontWeight: '600' }}>
          🌐 Visit showingbooker.com • 📧 Share this page • 💬 Tell your network
        </p>
      </CTASection>

      <ContentSection>
        <SectionTitle>📧 Sample Letter to Your Landlord/Agent</SectionTitle>
        <div style={{ 
          background: '#f8f9fa', 
          border: '2px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '25px', 
          margin: '20px 0',
          position: 'relative'
        }}>
          <div style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '15px', 
            background: '#667eea', 
            color: 'white', 
            padding: '5px 10px', 
            borderRadius: '4px', 
            fontSize: '12px',
            cursor: 'pointer'
          }}
          onClick={() => {
            const letterText = document.getElementById('tenant-letter').innerText;
            navigator.clipboard.writeText(letterText);
            alert('Letter copied to clipboard!');
          }}
          >
            📋 Copy Letter
          </div>
          
          <div id="tenant-letter" style={{ fontFamily: 'Times New Roman, serif', lineHeight: '1.8', color: '#333' }}>
            <p><strong>Dear [Landlord/Property Manager/Listing Agent Name],</strong></p>
            
            <p><strong>Date:</strong> [Today's Date]</p>
            
            <p><strong>Re: Property Showing Coordination Request - [Property Address]</strong></p>
            
            <p>I hope this message finds you well. I understand that the property I currently rent is listed for sale, and I want to cooperate fully as required by law while ensuring that everyone follows proper legal procedures.</p>
            
            <p>This is my home, and I want to feel safe, maintain my privacy, keep my belongings secure, and protect my legal rights during this process. To ensure professional standards and legal compliance, I respectfully request that we use <strong>ShowingBooker.com</strong> for all property showings.</p>
            
            <p><strong>ShowingBooker provides the following benefits:</strong></p>
            <ul style={{ marginLeft: '20px' }}>
              <li>✅ <strong>Legal Compliance:</strong> Automatic 48+ hour advance notice (exceeding CA Civil Code Section 1954 requirements)</li>
              <li>✅ <strong>Professional Documentation:</strong> Email confirmations, SMS notifications, and compliance records</li>
              <li>✅ <strong>Scheduled Coordination:</strong> 30-minute appointment slots during normal business hours</li>
              <li>✅ <strong>Tenant Protection:</strong> Ensures I can be present or reserve the right to be present to protect my belongings and privacy</li>
              <li>✅ <strong>No Cost to You:</strong> Free for owners and listing agents to use</li>
            </ul>
            
            <p><strong>My specific requirements for showings:</strong></p>
            <ul style={{ marginLeft: '20px' }}>
              <li>🚫 <strong>No open houses</strong> to the general public</li>
              <li>🚫 <strong>No broker caravans</strong> or group agent tours</li>
              <li>🚫 <strong>No photography</strong> of my personal belongings for online publication</li>
              <li>🏠 <strong>Individual appointments only</strong> during normal business hours</li>
              <li>👥 <strong>I will be present OR reserve the right to be present</strong> during all showings to ensure security and compliance</li>
              <li>⏰ <strong>30-minute maximum</strong> showing duration per appointment</li>
            </ul>
            
            <p>ShowingBooker.com handles all the coordination professionally with a $15 service fee that is paid by the buyer's agent and/or buyer to the platform for comprehensive compliance documentation and providing all required legal notices through their third-party platform. This service fee covers:</p>
            
            <ul style={{ marginLeft: '20px' }}>
              <li>📋 <strong>Legal Notice Generation:</strong> Automated CA Civil Code Section 1954 compliant notices</li>
              <li>📧 <strong>Multi-Channel Communication:</strong> Email confirmations, SMS notifications, and optional certified mail</li>
              <li>📂 <strong>Compliance Documentation:</strong> Complete paper trail for all parties' protection</li>
              <li>⏰ <strong>Scheduling Coordination:</strong> Professional appointment management and conflict prevention</li>
              <li>🔒 <strong>Privacy Protection:</strong> Secure handling of personal information and showing details</li>
              <li>📞 <strong>Customer Support:</strong> Professional assistance for any coordination issues</li>
            </ul>
            
            <p>This professional third-party service ensures quality, accountability, and legal compliance while protecting both your interests and my legal rights. The fee structure incentivizes serious, professional showings while deterring casual or inappropriate requests.</p>
            
            <p>I believe this approach will make the showing process smoother for everyone involved while ensuring full legal compliance. Please let me know when you have set up the property on ShowingBooker so we can coordinate effectively.</p>
            
            <p>Thank you for your understanding and cooperation in maintaining professional standards during this process.</p>
            
            <p><strong>Sincerely,</strong></p>
            <br/>
            <p>[Your Full Name]<br/>
            [Your Phone Number]<br/>
            [Your Email Address]<br/>
            [Date]</p>
            
            <p style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
              <em>This letter references California Civil Code Section 1954 regarding landlord right of entry. For more information about tenant rights, visit showingbooker.com/tenant-faq</em>
            </p>
          </div>
        </div>
        
        <div style={{ 
          background: '#e3f2fd', 
          padding: '15px', 
          borderRadius: '8px', 
          margin: '20px 0',
          border: '1px solid #90caf9'
        }}>
          <p style={{ margin: '0', fontWeight: '600', color: '#0277bd' }}>
            💡 <strong>Pro Tip:</strong> Send this letter via email AND text message to create a paper trail. 
            Keep copies for your records as documentation of your cooperation and legal compliance requests.
          </p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionTitle>🤝 How to Get Your Agent Using ShowingBooker</SectionTitle>
        <div style={{ lineHeight: '1.6' }}>
          <p><strong>Step 1:</strong> Use the sample letter above to formally request ShowingBooker</p>
          <p><strong>Step 2:</strong> Share this page with your landlord, property manager, or agent</p>
          <p><strong>Step 3:</strong> Explain that you expect professional showing standards with proper notice</p>
          <p><strong>Step 4:</strong> Let them know other tenants and agents are already using ShowingBooker successfully</p>
          <p><strong>Step 5:</strong> If they resist, remind them of your legal rights and the importance of documentation</p>
          <p><strong>Step 6:</strong> Consider referring other tenants to build network effects and earn referral income</p>
        </div>
      </ContentSection>
    </Container>
  );
};

export default TenantFAQ;